import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { useLanguage } from '@/context/LanguageContext';
import { useFieldVisibility } from '@/context/FieldVisibilityContext';
import { cn, formatDateTime, formatDuration, debounce } from '@/utils';
import type { CallRecord, SortState, FilterState, DateFilter, UrgencyLevel } from '@/types';
import { URGENCY_CONFIG } from '@/types';
import {
  Search,
  Filter,
  Download,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  CheckCircle,
  XCircle,
  Play,
  FileText,
} from 'lucide-react';

interface DataTableProps {
  records: CallRecord[];
  loading: boolean;
  pagination: { page: number; pageSize: number; total: number };
  onPaginationChange: (page: number, pageSize: number) => void;
  onSort: (column: keyof CallRecord) => void;
  sortState: SortState;
  filterState: FilterState;
  onFilterChange: (state: Partial<FilterState>) => void;
  onView: (record: CallRecord) => void;
  onStatusToggle: (id: string, status: boolean) => Promise<void>;
  onExportCSV: () => void;
  onExportExcel: () => void;
  categories: string[];
  serviceTypes: string[];
  columns?: string[];
}

export function DataTable({
  records,
  loading,
  pagination,
  onPaginationChange,
  onSort,
  sortState,
  filterState,
  onFilterChange,
  onView,
  onStatusToggle,
  onExportCSV,
  onExportExcel,
  categories,
  serviceTypes,
  columns = [],
}: DataTableProps) {
  const { language, t } = useLanguage();
  const { getVisibleFields } = useFieldVisibility();
  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState(filterState.search);

  // Debounced search handler - 300ms delay
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        onFilterChange({ search: value });
      }, 300),
    [onFilterChange]
  );

  // Handle search input change with debouncing
  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    debouncedSearch(value);
  }, [debouncedSearch]);

  // Sync local search with filter state when it changes externally
  useEffect(() => {
    setSearchInput(filterState.search);
  }, [filterState.search]);

  // Get visible fields for table context
  const visibleFields = getVisibleFields('table');

  // Column definitions based on visibility
  const getColumnWidth = (key: string): string => {
    const widths: Record<string, string> = {
      id: '100px',
      created_at: '160px',
      name: '140px',
      phone_number: '140px',
      category: '120px',
      service_type: '120px',
      inquiry: '180px',
      call_duration_sec: '90px',
      urgency: '100px',
      status: '100px',
      notes: '180px',
      call_summary: '200px',
      conversation_id: '150px',
      audio_uri: '100px',
    };
    return widths[key] || '120px';
  };

  const getColumnLabel = (key: string): string => {
    // For dynamic columns, format the column name nicely
    if (columns.length > 0 && !['id', 'created_at', 'name', 'phone_number', 'category', 'service_type', 'inquiry', 'call_duration_sec', 'urgency', 'status', 'notes', 'call_summary', 'conversation_id', 'audio_uri'].includes(key)) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }
    
    const labelKeys: Record<string, string> = {
      id: 'field.id',
      created_at: 'field.date',
      name: 'field.name',
      phone_number: 'field.phone',
      category: 'field.category',
      service_type: 'field.service',
      inquiry: 'field.inquiry',
      call_duration_sec: 'field.duration',
      urgency: 'field.urgency',
      status: 'field.status',
      notes: 'field.notes',
      call_summary: 'field.summary',
      conversation_id: 'field.conversationId',
      audio_uri: 'field.audio',
    };
    return t(labelKeys[key] || key);
  };

  // Filter columns based on visibility - use dynamic columns if available
  const defaultColumns = [
    'created_at', 'name', 'phone_number', 'category', 'service_type',
    'inquiry', 'call_duration_sec', 'urgency', 'status', 'notes',
    'call_summary', 'conversation_id', 'audio_uri'
  ];
  
  // Use dynamic columns from props, or fall back to default
  const columnOrder = columns.length > 0 
    ? columns.filter(col => col !== 'id') // Filter out id, we'll add it at the end if needed
    : defaultColumns;

  // Always include actions column, show all columns for dynamic tables or filter based on visibility settings for alsalama_call_records
  const visibleColumns = [...columnOrder, 'actions'].filter(key => {
    if (key === 'actions') return true;
    
    // If there's no visibility config for this table yet, show all columns by default
    if (Object.keys(visibleFields).length === 0) return true;
    
    // Otherwise, use visibility settings
    return visibleFields.includes(key as any);
  });

  // Handle date filter changes
  const handleDateFilterChange = (from: string | null, to: string | null) => {
    const newDateFilter: DateFilter = {
      preset: from || to ? 'custom' : null,
      from,
      to,
    };
    onFilterChange({ dateFilter: newDateFilter });
  };

  // Pagination helpers
  const totalPages = Math.ceil(pagination.total / pagination.pageSize);
  const startRecord = pagination.total === 0 ? 0 : pagination.page * pagination.pageSize + 1;
  const endRecord = Math.min((pagination.page + 1) * pagination.pageSize, pagination.total);

  // Helper to render cell content based on field type
  const renderCellContent = (record: CallRecord, key: string): React.ReactNode => {
    const value = record[key as keyof CallRecord];

    switch (key) {
      case 'created_at':
        return formatDateTime(record.created_at, language);
      case 'call_duration_sec':
        return formatDuration(record.call_duration_sec);
      case 'urgency':
        const urgencyLabelKeys: Record<string, string> = {
          low: 'urgency.low',
          medium: 'urgency.medium',
          high: 'urgency.high',
          extra_high: 'urgency.extraHigh',
          no: 'urgency.no',
        };
        return (
          <span
            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
            style={{
              backgroundColor: URGENCY_CONFIG[record.urgency]?.bg || URGENCY_CONFIG.low.bg,
              color: URGENCY_CONFIG[record.urgency]?.color || URGENCY_CONFIG.low.color,
            }}
          >
            {t(urgencyLabelKeys[record.urgency] || 'urgency.low')}
          </span>
        );
      case 'status':
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onStatusToggle(record.id, !record.status);
            }}
            className={cn(
              'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all',
              record.status === true
                ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
            )}
          >
            {record.status === true ? (
              <CheckCircle className="h-3 w-3" />
            ) : (
              <XCircle className="h-3 w-3" />
            )}
            {record.status === true ? t('status.resolved') : record.status === false ? t('status.pending') : '-'}
          </button>
        );
      case 'category':
        return record.category && <Badge variant="secondary">{record.category}</Badge>;
      case 'service_type':
        return record.service_type && <Badge variant="outline">{record.service_type}</Badge>;
      case 'inquiry':
      case 'notes':
      case 'call_summary':
        return (
          <span className="truncate block" title={value as string | undefined}>
            {value || '-'}
          </span>
        );
      case 'name':
        return <span className="font-medium">{value || '-'}</span>;
      case 'phone_number':
        return <span className="text-muted-foreground">{value || '-'}</span>;
      default:
        return value as string;
    }
  };

  if (loading && records.length === 0) {
    return (
      <Card className="p-12 text-center">
        <div className="animate-pulse-soft">
          <div className="w-12 h-12 bg-muted rounded-full mx-auto mb-4" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      {/* Toolbar */}
      <div className="p-4 border-b space-y-4">
        <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between">
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <Input
              placeholder={t('placeholder.search')}
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              icon={<Search className="h-4 w-4" />}
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2"
            >
              <Filter className="h-4 w-4" />
              {t('common.filter')}
              {(filterState.category || filterState.serviceType !== null || filterState.status !== null || filterState.urgency !== null || filterState.dateFilter?.from || filterState.dateFilter?.to) && (
                <span className="w-2 h-2 rounded-full bg-primary" />
              )}
            </Button>
            <Button variant="outline" size="sm" onClick={onExportCSV} className="gap-2">
              <Download className="h-4 w-4" />
              CSV
            </Button>
            <Button variant="outline" size="sm" onClick={onExportExcel} className="gap-2">
              <FileText className="h-4 w-4" />
              Excel
            </Button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg animate-fade-in">
            <div className="col-span-1 sm:col-span-2 lg:col-span-4">
              <label className="block text-xs font-medium text-muted-foreground mb-1">Date Range</label>
              <DateRangePicker
                from={filterState.dateFilter?.from || null}
                to={filterState.dateFilter?.to || null}
                onChange={handleDateFilterChange}
              />
            </div>
            <Select
              label="Category"
              placeholder={t('filter.selectCategory')}
              value={filterState.category || ''}
              onChange={(e) => onFilterChange({ category: e.target.value || null })}
              options={categories.map(c => ({ value: c, label: c }))}
            />
            <Select
              label="Service Type"
              placeholder={t('filter.selectServiceType')}
              value={filterState.serviceType || ''}
              onChange={(e) => onFilterChange({ serviceType: e.target.value || null })}
              options={serviceTypes.map(s => ({ value: s, label: s }))}
            />
            <Select
              label="Status"
              placeholder={t('status.all')}
              value={filterState.status === null ? '' : filterState.status ? 'resolved' : 'pending'}
              onChange={(e) => {
                const value = e.target.value;
                onFilterChange({
                  status: value === '' ? null : value === 'resolved',
                });
              }}
              options={[
                { value: 'resolved', label: t('status.resolved') },
                { value: 'pending', label: t('status.pending') },
              ]}
            />
            <Select
              label="Urgency"
              placeholder="All"
              value={filterState.urgency || ''}
              onChange={(e) => onFilterChange({ urgency: (e.target.value as UrgencyLevel) || null })}
              options={[
                { value: 'low', label: 'Low' },
                { value: 'medium', label: 'Medium' },
                { value: 'high', label: 'High' },
                { value: 'extra_high', label: 'Extra High' },
                { value: 'no', label: 'No' },
              ]}
            />
            <div className="flex items-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  onFilterChange({
                    category: null,
                    serviceType: null,
                    status: null,
                    urgency: null,
                    search: '',
                    dateFilter: { preset: null, from: null, to: null },
                  })
                }
              >
                {t('filter.clear')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 sticky top-0">
            <tr>
              {visibleColumns.map((col) => (
                <th
                  key={col}
                  className={cn(
                    'px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap text-left',
                    col === 'actions' && 'w-[100px]'
                  )}
                  style={{ width: col === 'actions' ? '100px' : getColumnWidth(col) }}
                >
                  {col === 'actions' ? (
                    t('table.actions')
                  ) : col === 'inquiry' ? (
                    t('filter.inquiry')
                  ) : (
                    <button
                      className="flex items-center gap-1 hover:text-foreground transition-colors"
                      onClick={() => onSort(col as keyof CallRecord)}
                    >
                      {getColumnLabel(col)}
                      {sortState.column === col ? (
                        sortState.direction === 'asc' ? (
                          <ChevronUp className="h-3 w-3" />
                        ) : (
                          <ChevronDown className="h-3 w-3" />
                        )
                      ) : (
                        <div className="h-3 w-3 flex flex-col opacity-30">
                          <ChevronUp className="h-1.5 w-1.5 -mb-0.5" />
                          <ChevronDown className="h-1.5 w-1.5" />
                        </div>
                      )}
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {records.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length + 1} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                      <Search className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground">{t('empty.noRecords')}</p>
                    <p className="text-sm text-muted-foreground">{t('empty.noRecordsDesc')}</p>
                  </div>
                </td>
              </tr>
            ) : (
              records.map((record) => (
                <tr
                  key={record.id}
                  onClick={() => onView(record)}
                  className="hover:bg-muted/50 transition-colors cursor-pointer"
                >
                  {visibleColumns.map((col) => (
                    <td
                      key={col}
                      className={cn(
                        'px-4 py-3 whitespace-nowrap text-sm',
                        ['inquiry', 'notes', 'call_summary'].includes(col) && 'max-w-[180px]'
                      )}
                    >
                      {col === 'actions' ? (
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => onView(record)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {record.audio_uri && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => window.open(record.audio_uri!, '_blank', 'noopener,noreferrer')}
                              title={t('btn.playAudio')}
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ) : (
                        renderCellContent(record, col)
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="border-t px-3 py-3 flex flex-col sm:flex-row gap-3 sm:gap-0 justify-between items-center">
          <div className="text-sm text-muted-foreground">
            {t('table.showing')} <strong>{startRecord}-{endRecord}</strong> {t('table.of')}{' '}
            <strong>{pagination.total}</strong> {t('table.records')}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={String(pagination.pageSize)}
              onChange={(e) => {
                e.preventDefault();
                const newPageSize = Number(e.target.value);
                onPaginationChange(0, newPageSize);
              }}
              className="h-8 w-32 px-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="10">10 / page</option>
              <option value="25">25 / page</option>
              <option value="50">50 / page</option>
              <option value="100">100 / page</option>
            </select>
            <Button
              variant="outline"
              size="icon"
              onClick={() => onPaginationChange(pagination.page - 1, pagination.pageSize)}
              disabled={pagination.page === 0}
              className="h-8 w-8"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              {pagination.page + 1} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={() => onPaginationChange(pagination.page + 1, pagination.pageSize)}
              disabled={pagination.page >= totalPages - 1}
              className="h-8 w-8"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
